import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';

import {
  buildYouTubeEmbedSrcFromPageUrl,
  extractYouTubeVideoId,
} from '../../../../utils/youtubeEmbed.utils';
import { getSignedMediaUrl } from '../../../../services/media.service';
import { isBitmovinPlayerEnabled } from '../../../../utils/videoPlayerFeatureFlag';

const bitmovinKey = process.env.REACT_APP_BITMOVIN_PLAYER_KEY;

function parseTimestampToSeconds(timestamp) {
  if (timestamp == null || timestamp === '') return undefined;
  if (typeof timestamp === 'number' && !Number.isNaN(timestamp)) {
    return Math.max(0, timestamp);
  }
  const s = String(timestamp).trim();
  if (/^\d+(\.\d+)?$/.test(s)) {
    return Math.max(0, Number.parseFloat(s));
  }
  const parts = s.split(':').map((p) => Number.parseFloat(p));
  if (parts.some((n) => Number.isNaN(n))) return undefined;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return undefined;
}

function timestampToStartSeconds(timestamp) {
  const sec = parseTimestampToSeconds(timestamp);
  return sec === undefined ? undefined : sec;
}

function buildSource(url) {
  if (!url || typeof url !== 'string') return null;
  const cleanUrl = url.split('?')[0].toLowerCase();
  if (cleanUrl.endsWith('.m3u8')) {
    return { hls: url };
  }
  return { progressive: url };
}

function Resultvideo() {
  const location = useLocation();
  const navigate = useNavigate();
  const { videoId, timestamp, videoUrl, upload_view_type, assessmentId } = location.state || {};

  const [resolveStatus, setResolveStatus] = useState('idle');
  const [resolveError, setResolveError] = useState(null);
  const [isYouTube, setIsYouTube] = useState(false);
  const [resolvedPlayUrl, setResolvedPlayUrl] = useState(null);
  const [useBitmovin, setUseBitmovin] = useState(() => isBitmovinPlayerEnabled());

  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);

  useEffect(() => {
    const refreshPlayerFlag = () => setUseBitmovin(isBitmovinPlayerEnabled());
    window.addEventListener('storage', refreshPlayerFlag);
    return () => window.removeEventListener('storage', refreshPlayerFlag);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function resolvePlaybackUrl() {
      setResolveStatus('loading');
      setResolveError(null);
      setResolvedPlayUrl(null);
      setIsYouTube(false);

      if (!location.state || (!videoUrl && (videoId == null || videoId === ''))) {
        if (!cancelled) {
          setResolveStatus('error');
          setResolveError('No video data. Use Play Video from the assessment results page.');
        }
        return;
      }

      try {
        const yt = upload_view_type === 'youtube' || Boolean(extractYouTubeVideoId(videoUrl || ''));
        if (yt && videoUrl) {
          if (!cancelled) {
            setIsYouTube(true);
            setResolvedPlayUrl(videoUrl);
            setResolveStatus('ready');
          }
          return;
        }

        if (upload_view_type === 'src' && videoUrl) {
          if (!cancelled) {
            setResolvedPlayUrl(videoUrl);
            setResolveStatus('ready');
          }
          return;
        }

        if (videoId != null && String(videoId) !== '') {
          const url = await getSignedMediaUrl({
            type: 'video',
            mediaId: Number(videoId),
          });
          if (cancelled) return;
          if (url) {
            setResolvedPlayUrl(url);
            setResolveStatus('ready');
            return;
          }
        }

        if ((videoId == null || String(videoId) === '') && videoUrl && /^https?:\/\//i.test(String(videoUrl).trim())) {
          if (!cancelled) {
            setResolvedPlayUrl(videoUrl.trim());
            setResolveStatus('ready');
          }
          return;
        }

        if (!cancelled) {
          setResolveStatus('error');
          setResolveError('Could not load a playable video URL.');
        }
      } catch (e) {
        if (!cancelled) {
          setResolveStatus('error');
          setResolveError(e?.message || 'Failed to resolve video.');
        }
      }
    }

    resolvePlaybackUrl();
    return () => {
      cancelled = true;
    };
  }, [location.key, videoId, videoUrl, upload_view_type]);

  const handleBack = () => {
    if (assessmentId) {
      navigate(`/user/courses/Result?assessmentId=${encodeURIComponent(assessmentId)}`);
    } else {
      navigate(-1);
    }
  };

  const jumpToTimestamp = useCallback(() => {
    if (!playerRef.current || !timestamp) return;
    const timeInSeconds = parseTimestampToSeconds(timestamp);
    if (timeInSeconds == null) return;
    const playPromise = typeof playerRef.current.seek === 'function'
      ? (playerRef.current.seek(timeInSeconds), playerRef.current.play())
      : (playerRef.current.currentTime = timeInSeconds, playerRef.current.play());
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.catch(() => {});
    }
  }, [timestamp]);

  const initAndLoadBitmovin = useCallback(() => {
    if (!window.bitmovin?.player || !playerContainerRef.current || !resolvedPlayUrl) return;

    const source = buildSource(resolvedPlayUrl);
    if (!source) return;

    try {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }

      const playerConfig = {
        key: bitmovinKey,
        playback: {
          autoplay: true,
          muted: true,
        },
        style: {
          width: '100%',
          aspectratio: '16:9',
        },
        ui: {
          playbackSpeedSelection: true,
          watermark: false,
          customColors: {
            timeline: '#3152e8',
            buttons: '#3152e8',
          },
        },
      };

      const player = new window.bitmovin.player.Player(playerContainerRef.current, playerConfig);
      playerRef.current = player;

      player
        .load(source)
        .then(() => {
          const startSec = parseTimestampToSeconds(timestamp);
          if (startSec != null && startSec >= 0) {
            player.seek(startSec);
          }
          const playPromise = player.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise.catch(() => {});
          }
        })
        .catch(() => setUseBitmovin(false));
    } catch {
      setUseBitmovin(false);
    }
  }, [resolvedPlayUrl, timestamp]);

  useEffect(() => {
    if (isYouTube || !useBitmovin || resolveStatus !== 'ready' || !resolvedPlayUrl) {
      return undefined;
    }

    const ensureCss = () => {
      if (!document.querySelector('link[href*="bitmovinplayer-ui.css"]')) {
        const link = document.createElement('link');
        link.href = 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer-ui.css';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
    };

    ensureCss();

    const run = () => initAndLoadBitmovin();

    let script = document.querySelector('script[src*="bitmovinplayer.js"]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer.js';
      script.async = true;
      script.onload = run;
      document.body.appendChild(script);
    } else {
      run();
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [isYouTube, resolveStatus, resolvedPlayUrl, initAndLoadBitmovin, useBitmovin]);

  if (resolveStatus === 'loading' || resolveStatus === 'idle') {
    return (
      <div className="tw-w-full tw-max-w-[1200px] tw-m-[1rem_auto] tw-p-3 sm:tw-p-4 tw-flex tw-flex-col result-video-container">
        <div className={"[&_p]:tw-mb-4 [&_p]:tw-text-[#666] [&_p]:tw-text-[1.1rem] tw-text-center tw-p-8 tw-bg-[#fff] tw-rounded-[8px] tw-shadow-[0_2px_4px_rgba(0,_0,_0,_0.1)] tw-relative tw-mx-auto tw-w-full tw-text-[var(--error-color)] tw-text-[12px] tw-leading-[18px] tw-mt-[2px] error-message"}>
          <p>Loading video...</p>
          <button type="button" onClick={handleBack} className={"tw-flex tw-items-center tw-gap-2 tw-p-[0.5rem_1rem] tw-bg-[#f0f0f0] tw-text-[#333] tw-border-0 tw-rounded-[4px] tw-cursor-pointer tw-text-[0.9rem] [transition:all_0.2s] [&:hover]:tw-bg-[#e0e0e0] back-btn"}>
            <Icon icon="mdi:arrow-left" /> Go Back
          </button>
        </div>
      </div>
    );
  }

  if (resolveStatus === 'error' || !resolvedPlayUrl) {
    return (
      <div className="tw-w-full tw-max-w-[1200px] tw-m-[1rem_auto] tw-p-3 sm:tw-p-4 tw-flex tw-flex-col result-video-container">
        <div className={"[&_p]:tw-mb-4 [&_p]:tw-text-[#666] [&_p]:tw-text-[1.1rem] tw-text-center tw-p-8 tw-bg-[#fff] tw-rounded-[8px] tw-shadow-[0_2px_4px_rgba(0,_0,_0,_0.1)] tw-relative tw-mx-auto tw-w-full tw-text-[var(--error-color)] tw-text-[12px] tw-leading-[18px] tw-mt-[2px] error-message"}>
          <p>{resolveError || 'Video URL not found'}</p>
          <button type="button" onClick={handleBack} className={"tw-flex tw-items-center tw-gap-2 tw-p-[0.5rem_1rem] tw-bg-[#f0f0f0] tw-text-[#333] tw-border-0 tw-rounded-[4px] tw-cursor-pointer tw-text-[0.9rem] [transition:all_0.2s] [&:hover]:tw-bg-[#e0e0e0] back-btn"}>
            <Icon icon="mdi:arrow-left" /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-max-w-[1200px] tw-m-[1rem_auto] tw-p-3 sm:tw-p-4 tw-flex tw-flex-col result-video-container">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-6 max-[768px]:tw-flex-col max-[768px]:tw-gap-4 video-header">
        <button type="button" onClick={handleBack} className={"tw-flex tw-items-center tw-gap-2 tw-p-[0.5rem_1rem] tw-bg-[#f0f0f0] tw-text-[#333] tw-border-0 tw-rounded-[4px] tw-cursor-pointer tw-text-[0.9rem] [transition:all_0.2s] [&:hover]:tw-bg-[#e0e0e0] back-btn"}>
          <Icon icon="mdi:arrow-left" /> Back to Results
        </button>
        {!isYouTube && (
          <div className="tw-flex tw-items-center tw-gap-4 max-[768px]:tw-w-full max-[768px]:tw-justify-between video-controls">
            <button type="button" onClick={jumpToTimestamp} className={"tw-flex tw-items-center tw-gap-2 tw-p-[0.5rem_1rem] tw-bg-[#2196f3] tw-text-[white] tw-border-0 tw-rounded-[4px] tw-cursor-pointer tw-text-[0.9rem] [transition:all_0.2s] [&:hover]:tw-bg-[#1976d2] [&_svg]:tw-text-[1.2rem] replay-btn"}>
              <Icon icon="mdi:replay" /> Replay from {timestamp || '-'}
            </button>
            <span className="tw-text-[#666] tw-text-[0.9rem] tw-p-2 tw-bg-[#f5f5f5] tw-rounded-[4px] timestamp-display">Timestamp: {timestamp || '-'}</span>
          </div>
        )}
      </div>

      <div className="tw-w-full tw-aspect-video tw-bg-[#000] tw-rounded-[8px] tw-overflow-hidden tw-shadow-[0_2px_4px_rgba(0,_0,_0,_0.1)] tw-relative video-wrapper">
        {isYouTube ? (
          <div className={"tw-relative [aspect-ratio:16_/_9] tw-w-full [&_>_:is(iframe,_video,_div)]:tw-absolute [&_>_:is(iframe,_video,_div)]:tw-inset-0 [&_>_:is(iframe,_video,_div)]:tw-h-full [&_>_:is(iframe,_video,_div)]:tw-w-full ui-video-frame"}>
            <iframe
              title="Result Video"
              src={buildYouTubeEmbedSrcFromPageUrl(resolvedPlayUrl, {
                startSeconds: timestampToStartSeconds(timestamp),
                autoplay: Boolean(timestamp),
              })}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              style={{ width: '100%', borderRadius: '8px' }}
            />
          </div>
        ) : useBitmovin ? (
          <div ref={playerContainerRef} className={"!tw-absolute tw-top-0 tw-left-0 !tw-w-full !tw-h-full [&_video]:!tw-w-full [&_video]:!tw-h-full [&_video]:!tw-object-contain bitmovin-player"} />
        ) : (
          <video
            key={`native-${resolvedPlayUrl}`}
            ref={playerRef}
            aria-label="Assessment reference video"
            autoPlay
            muted
            controls
            preload="metadata"
            src={resolvedPlayUrl}
            onLoadedMetadata={(event) => {
              const startSec = parseTimestampToSeconds(timestamp);
              if (startSec != null) event.currentTarget.currentTime = startSec;
            }}
            style={{ width: '100%', aspectRatio: '16 / 9', background: '#000', borderRadius: '8px' }}
          />
        )}
      </div>
    </div>
  );
}

export default Resultvideo;
